'use client';

import { useEffect, useState } from 'react';
import { useTheme } from '@/app/_lib/providers/ThemeProvider';
import LogoSvg from './LogoSvg';

interface ThemeAwareLogoProps {
  className?: string;
}

// Inner component with theme context
function ThemeAwareLogoInner({ className = '' }: ThemeAwareLogoProps) {
  const { resolvedTheme } = useTheme();

  // Force a re-render when theme changes to ensure logo updates
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    setForceUpdate((prev) => prev + 1);
  }, [resolvedTheme]);

  // Logic to determine logo color mode:
  // - In dark theme: always use 'light' mode for logo
  // - In light theme + (scrolled or inner page): use 'dark' mode for logo
  // - In light theme + (not scrolled and not inner page): use 'light' mode for logo

  let logoColorMode: 'light' | 'dark' | 'all-light' = 'light';

  if (resolvedTheme === 'dark') {
    // Always light in dark mode - logo should be light colored
    logoColorMode = 'light';
  } else {
    // In light mode, logo color depends on scroll position/inner page
    logoColorMode = 'dark';
  }

  return <LogoSvg colorMode={logoColorMode} className={className} />;
}

// Export the inner component directly
export function ThemeAwareLogo(props: ThemeAwareLogoProps) {
  // This ensures the component only renders on the client
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    // Render a placeholder with the same dimensions during SSR
    return <div className={props.className} style={{ height: '100%', width: '100%' }} />;
  }

  return <ThemeAwareLogoInner {...props} />;
}
